import {ReactNode, useEffect, useRef} from 'react';
import {cacheData, cacheFetch, debounce, funcName, funcOrValOr, get_dur, getAgo, MS_MIN} from '../tools/CommonJS';
import {isDemo, query, QueryParams, QueryReturn} from '../tools/CommonHtml';
import {componentInit, useDataSingleton, useStateEx} from '../tools/CommonReact';
import {ApiParams, ApiParamsMap, ApiRspMap} from './ApiParamsTypes';
import {applyDemoOverlay, applyDemoParamOverlay} from './DemoOverlays';
import {LevelNavigation} from '../tools/LevelNavigation';

export const isAWS = import.meta.env?.VITE_TARGET?.toLowerCase() === 'aws' || undefined;
export const isOnPrem = import.meta.env?.VITE_TARGET?.toLowerCase() === 'onprem' || undefined;
export const isDev = import.meta.env?.VITE_ENV?.toLowerCase().startsWith('dev') || undefined;
export const isAwsDev = import.meta.env?.VITE_ENV?.toLowerCase() === 'devaws' || undefined;
export const isTalDemo = isDemo;

export const conLog = (...args) => isDev && console.log(`${new Date().toLocaleTimeString().toLocaleLowerCase()} ${
	get_dur(performance.now(), {trimLeftZero: 1, addUnit: 1, showMs: 1})} ${funcName(3, {nonNull: 1})}:`, ...args);


interface AppContext {
	sid: string;
	auth?: {
		getIdJwtToken: () => Promise<string>;
	};
	setDlg: (props: object) => number;
	setMsg: (msg: ReactNode) => number;
	setMsgEx: (props) => number;
	isPanelOn: (key: string) => boolean;
	tooltip: {
		set: (prop: any) => void;
		isShown: () => boolean;
		tmr: NodeJS.Timeout | string | number | undefined;
	};
	socket: {
		on: (socket: string, cb: (args: any) => void) => void;
		off: (socket: string, cb: (args: any) => void) => void;
	};
	nav: LevelNavigation;       // page-level navigation helpers from useLevelNavigation
}

export const appCtx: AppContext = {
	sid: `${Date.now()}_${Math.random() * 10000 | 0}`,       // custom Session ID, e.g.: maintain tab ID with server.
	setDlg: () => 0,
	setMsg: () => 0,
	setMsgEx: () => 0,
	isPanelOn: () => false,
	tooltip: {
		set: () => {},
		isShown: () => false,
		tmr: undefined,
	},
	socket: {
		on: () => {},
		off: () => {}
	},
	nav: null           // set by Main
};

export const msgBox = msg => appCtx.setMsg(msg);
export const msgBoxEx = obj => appCtx.setMsgEx(obj);
export const isMsgBoxOn = key => appCtx.isPanelOn(key);

export {genTooltip, genTooltipIfClamped, showTooltip, hideTooltip, isTooltipOn} from '../tools/Tooltip';

export const makeAuthHeader = headers => async () => ({
	...isAWS || isAwsDev || isOnPrem? {Authorization: `Bearer ${isAWS || isOnPrem? await appCtx.auth?.getIdJwtToken() : 'someStaticBearerString'}`} :
		{secid: sessionStorage.getItem('secid')},
	...await funcOrValOr(headers)
});


export function queryAuth<K extends keyof ApiParamsMap & string>({
	params = {}, onRsp, headers, ...props
}: QueryParams<K>): QueryReturn {
	return query({
		params,
		...props,
		headers: makeAuthHeader(headers),
		onRsp: (rsp: Response, reqParams: any) => {
			const secid = rsp.headers?.get('secid');
			if (secid)
				sessionStorage.setItem('secid', secid); // Save user id key for reuse in this session.
			return onRsp?.(rsp, reqParams);
		}
	});
}

let prevErrBoxId;       // prevents showing another error message when previous one still on.

export interface QueryOrErrParams<K extends keyof ApiParamsMap & string> extends QueryParams<K> {
	rsp?: (rspJson: ApiRspMap<K>, reqParams: ApiParams<K>) => void;
	onLoading?: (isLoading: boolean) => void;
	keepLoading?: (rspJson: any) => boolean;
	ignoreErr?: number | boolean;
	noErrMsg?: boolean | ((rspJson: any) => boolean);
	onErr?: (rspJson: any, reqParams: any) => void;
	[key: string]: any; // Allow additional properties
}

export function queryOrErr<K extends keyof ApiParamsMap & string>(
		{
			api, params, rsp, alt, ignoreErr, onLoading, keepLoading, noErrMsg, onErr, ...props
		}: QueryOrErrParams<K>): QueryReturn {
	api = funcOrValOr<K, undefined>(api) as K;
	onLoading?.(true);

	return queryAuth({
		api,
		params: applyDemoParamOverlay(api, params),
		alt,
		onJson: (rspJson: ApiRspMap<K>, reqParams: ApiParams<K>) => {
			if (!ignoreErr && rspJson?.error) {
				if (!funcOrValOr(noErrMsg, rspJson) && !isMsgBoxOn(prevErrBoxId))
					prevErrBoxId = msgBox(
						<div data-test='error-msgbox' className='flex flex-col gap-4'>
							<div data-test='error-msgbox-text' className='text-base text-sec'>
								{rspJson.error.toString?.()}
							</div>
							{!!rspJson.errText && <div className='text-sm'>{rspJson.errText}</div>}
						</div>);

				onErr?.(rspJson, reqParams);
			} else
				rsp?.(applyDemoOverlay(api, rspJson, params), reqParams);


			if (!keepLoading?.(rspJson))
				onLoading?.(false);

		},
		...props
	});
}

// Reuse a singular fetch data throughout the session. Any component using this with the api (serving as key)
// will get the most recent fetch, and refresh when any caller refreshes.
//
const FETCH_ERROR = Symbol('FETCH_ERROR');
export function useFetchDataSingleton({
	api, socket, resetOnLastUnuse, onData, pollMs, init, ...rest
}) {
	const vals = useRef<{tmPoll?: NodeJS.Timeout}>({}).current;
	const {val: rsp, refetch: refresh} = useDataSingleton({
		key: funcOrValOr(api),
		init,
		fetcher: onFetch => queryOrErr({api, ...rest, rsp: r => onFetch(onData?.(r) ?? r), onErr: () => onFetch(FETCH_ERROR)}),
		onCtor: refetch => {
			if (socket)
				appCtx.socket?.on(socket, refetch);
			if (pollMs)
				vals.tmPoll = setInterval(refetch, pollMs);
		},
		onDtor: refetch => {
			if (socket)
				appCtx.socket?.off(socket, refetch);
			clearInterval(vals.tmPoll);
		},
		resetOnLastUnuse
	});

	const isError = rsp === FETCH_ERROR;
	const isLoading = !rsp && !isError;

	return {rsp: isError? undefined : rsp, refresh, isError, isLoading};
}

export const c_defaultCacheMins = 10;

interface FetchDataCbVals {
	fetchData: (params: any) => any;
	objQs: Record<string, QueryOrErrParams<any>>;
	destroyed?: boolean;
	ord: number;
}
/**
 * Data fetch handler. Adds the following layers over queryOrErr:
 *  * Automatically cancels active queries if component dies.
 *  * UI data caching
 *
 *  @returns {fetchData({})}: Allows to perform any query anywhere in the component, and handles the cleanup if component is destroyed.
 *  @param api string - API url.
 *  @param params object?: parameters to pass to API.
 *  @param paramsCache object?: if provided, will be used for cache matching. Otherwise, cache will use normal params.
 *  @param cacheMins number of minutes to use cache without refetching query. If true, use default of 10 mins. If -1, ignore cache, but store result.
 *  @param cacheKeep function(rsp)?: if provided and cacheMins, return true if rsp should be cached.
 *  @param keepPrevQuery boolean? - Don't cancel previous query of the same API.
 *  @param onData function(rsp): called on fetched data or init if given. if onData returns a value, it replaces the returned rsp value.
 *  optional callback to check if response is invalid for cache and for passing to onData.
 *  forwarded parameters to queryOrErr: alt, onLoading, setMsg
 */
export function useFetchDataCb() {
	const vals = useRef<FetchDataCbVals>({objQs: {}, ord: 0, fetchData: () => {}}).current;

	if (componentInit(vals))
		vals.fetchData = ({
			api, params, paramsCache, cacheMins, onData, cacheKeep, keepPrevQuery, ...rest
		}) => {
			if (vals.destroyed) return;         // in case any debounce triggers.
			api = funcOrValOr(api);
			if (!keepPrevQuery)
				vals.objQs[api]?.cancel();

			const cacheKey = cacheMins && [api, paramsCache ?? params];
			if (cacheMins > 0) {
				cacheMins = typeof cacheMins === 'number'? cacheMins : c_defaultCacheMins;
				const cached = cacheFetch(cacheKey);
				if (cached) {
					conLog(`session cached ${getAgo(cached.created, {topOnly: 2})}: jKey: ${JSON.stringify(cacheKey)}`);
					const isOld = Date.now() - cached.created > cacheMins * MS_MIN;         // if cached query's age is over cacheMins
					if (isOld) cached.data.calcState = 1;           // Let caller know that refetch is being performed in the background
					rest.onLoading?.(true);                         // Match the network path: signal loading until the deferred onData lands, so a clear-then-refetch shows the spinner, not an empty-state flash.
					setTimeout(() => {
						onData?.(cached.data);
						if (!isOld) rest.onLoading?.(false);        // Fresh cache won't hit the network path below, so mirror its onLoading(false) here — else consumers stay stuck loading (e.g. empty-state never shows).
					});
					if (!isOld) return;                             // if age within cacheMins, don't refetch
				}
			}

			return vals.objQs[api + (!keepPrevQuery? '' : `_${vals.ord++}`)] = queryOrErr({
				api,
				params,
				rsp: (rsp, reqParams) => {
					if (cacheMins && !rsp?.isTest && (!cacheKeep || cacheKeep(rsp)))
						cacheData({key: cacheKey, data: rsp, created: rsp?.cacheTime && new Date(rsp.cacheTime)});
					onData?.(rsp, reqParams);
				},
				...rest
			});
		};


	useEffect(() => {
		vals.destroyed = false;         // useEffect destruct and init run on every hot-reload after editing.
		return () => {
			Object.values(vals.objQs).forEach(q => q.cancel());
			vals.destroyed = true;
		};
	}, []);

	return {fetchData: vals.fetchData, objQs: vals.objQs};
}

interface FetchDataParams<K extends keyof ApiParamsMap & string, R = ApiRspMap<K>> extends QueryOrErrParams<K> {
	socket?: string | boolean;
	onSocket?: (value?: unknown, params?: Record<string, any>) => void;
	cacheMins?: number | boolean;
	deps?: unknown[];
	init?: ApiRspMap<K>;
	pollMs?: number;
	onData?: (value: ApiRspMap<K>, isInit?: boolean) => R;
	noAutoFetch?: boolean;
	keepWhileRefetching?: boolean;
	cacheKeep?: (value?: unknown) => boolean;
}


/**
 * Uses useFetchDataCb for a specific API. Maintains last Response. Handles Socket.
 *  @param api function or string - API url.
 *  @param params object|function?: parameters to pass to API.
 *  @param socket string|boolean?: if specified, query will ask the backed to use cached data and apply socket when data refreshes. if true, socket will be api.replace("/", "_").
 *  @param onSocket function(args): if specified, unon socket trigger, will check if function returns false, will skip fetch.
 *  @param cacheMins number of minutes to use cache without refetching query. if true, use default of 10 mins.
 *  @param cacheKeep function(rsp)?: if provided and cacheMins, return true if rsp should be cached.
 *  @param deps function or array?: if specified, will refetch if any item in dependencies array changes.
 *  @param init object?: initial response before query returns. Useful for showing a placeholder on page load. If onData, passed thru onData.
 *  @param onData function(rsp, isInit)? called on fetched data or init if given. if onData returns a value, it replaces the returned rsp value.
 *  @param noAutoFetch if true, data won't be fetched immediately, but rather only after requested via the returned fetch func.
 *  @param keepWhileRefetching if true, previously fetched data won't be disposed, until replaced with the new fetch results.
 *  forwarded parameters to queryOrErr: alt, onLoading, setMsg
 *  @param alt object|function?: alternate response if api is 404 (good for dev before backend implements API)
 *  @param onLoading function(isLoading)? called (true) when issuing fetch, and again (false) when fetch is done.
 *  @returns {object} an object with these values:
 *   rsp: fetch result, or given alt if fetch 404, or given init before fetch returned. All 3 are passed thru onData if given.
 *   isInit: bool: true if init passed and before response returned
 *   isLoading: bool: reactive — true while a fetch is in-flight (or backend is recalculating, when keepLoading applies). Starts false.
 *   refresh(): callback to async refetch data.
 *   fetch(paramsAdd?): callback to async fetch data, with optional additional overriding params.
 *   silentClear(): purge current rsp/error in-place until the next fetch (no re-render; matches useVirtualPaging).
 */
export function useFetchData<K extends keyof ApiParamsMap & string, R = ApiRspMap<K>>(
		{
			api, params, socket, onSocket, pollMs, cacheMins, deps, init, onData,
			noAutoFetch, keepWhileRefetching, cacheKeep, onLoading, ...rest
		}: FetchDataParams<K, R>) {
	const clearData = () => ({data: !init ? null : onData ? onData(init, true) : init, isInit: !!init, error: undefined, isLoading: false});
	const {state: st, setState, setStateInRender} = useStateEx<any>(clearData);
	const {fetchData, objQs} = useFetchDataCb();
	Object.assign(st, {api, params, onData, onSocket, rest, deps, clearData, noAutoFetch, pollMs, onLoading});
	const checkUpdDeps = () => componentInit(st, [funcOrValOr(st.api), st.pollMs, ...funcOrValOr(st.deps) ?? []], '_init_useEffect');


	if (componentInit(st)) {
		st.fetchDataEx = (recalc = 0, paramsAdd?: Partial<ApiParams<K>>) => {
			const paramsCache = {...funcOrValOr(st.params), ...paramsAdd} as ApiParams<K>;
			checkUpdDeps();    // update init deps to prevent re-init, in case not called from useEffect.
			clearTimeout(st.tmPollSock);
			if (recalc >= 0) st.calcPollStart = st.calcPollDelay = undefined;       // user-initiated fetch: reset backoff cycle.
			fetchData({
				api: st.api,
				paramsCache,
				params: {...paramsCache, ...socket && {socket: st.socket, recalc: recalc >= 0 ? recalc : 0}},
				cacheMins: !recalc ? cacheMins : cacheMins && -1,    // -1 will not lookup UI cache, but will update it afterwards.
				cacheKeep: d => d && (!socket || d.calcState !== 2) && (!cacheKeep || cacheKeep(d)),
				keepLoading: d => socket && d?.calcState >= 1 && st.calcPolling,    // stop signaling loading once we give up polling.
				onLoading: (v: boolean) => (v && setState({isLoading: true}), st.onLoading?.(v)),    // start: trigger render. end: bundled into onData/onErr setStates below.
				onData: (d: ApiParams<K> & {calcState?: number}) => {
					clearTimeout(st.tmPollSock);            // reset any pending backoff poll from a prior onData (e.g. stale cached payload then network).
					let polling = false;
					if (d.calcState >= 1) {
						st.calcPollStart ??= Date.now();
						st.calcPollDelay = Math.min((st.calcPollDelay ?? 125) * 2, 4000);    // backoff: 250, 500, 1000, 2000, 4000, 4000... don't trust socket :(
						polling = Date.now() - st.calcPollStart < 10 * MS_MIN;              // keep polling only within the 10-min window.
						if (polling)
							st.tmPollSock = setTimeout(st.pollBackoff, st.calcPollDelay);   // give up polling after 10 mins; rely on socket / user refresh.
					} else
						st.calcPollStart = st.calcPollDelay = undefined;
					st.calcPolling = polling;
					const stillLoading = socket && polling;     // matches keepLoading: keep the spinner only while still actively polling the recompute.
					if (d.calcState !== 2)
						setState({
							data: st.onData ? st.onData(d, false) : d,
							isInit: false,
							noSrvCache: undefined,
							error: undefined,
							...!stillLoading && {isLoading: false}
						});
					else
						setState({noSrvCache: true, ...!stillLoading && {isLoading: false}});
				},
				onErr: error => setState({error, isLoading: false}),
				...st.rest
			});
		};

		st.fetch = (paramsAdd?: Partial<ApiParams<K>>, opts: {recalc?: number | boolean} = {}) => {
			if (!keepWhileRefetching) setState(st.clearData());
			st.fetchDataEx(opts.recalc || 0, paramsAdd);
		};

		st.cancelFetch = () => Object.values(objQs).forEach(q => q.cancel());
		st.pollFetch = () => st.fetchDataEx(-1);                // refetch - but don't ask backend to recalculate.
		st.pollBackoff = () => st.isFetching() || st.pollFetch();   // backoff poll: skip if a fetch is already in flight — its onData re-arms the backoff (don't cancel a slow cache refetch).
		st.refresh = (recalc = 1, paramsAdd) => st.fetch(paramsAdd, {recalc});
		st.isFetching = () => objQs[funcOrValOr(st.api)]?.isActive;
		st.cbSocket = (args: unknown) => st.onSocket?.(args) !== false && st.pollFetch();
		st.silentClear = () => {
			if (!st.data && st.error === undefined) return;
			Object.assign(st, st.clearData());
		};
	}

	if (checkUpdDeps()) {       // useEffect doesn't support variable list of deps.
		st.depOrd = (st.depOrd ?? 0) + 1;
		st.cancelFetch();
		if (!keepWhileRefetching)
			setStateInRender(st.clearData());
	}

    // useEffect destruct and init run on every hot-reload after editing! https://github.com/facebook/create-react-app/issues/7227
    // so if fetch didn't bring data after first run, you'll have to reload component to retry.
	useEffect(() => {
		if (socket) {
			st.socket = typeof socket === 'string'? socket :
				`${(funcOrValOr(api) as K).replace('/', '_')}_${Math.random() * 100000 |0}`;       // random to overcome same api with different params.
			appCtx.socket?.on(st.socket, st.cbSocket);
		}

		if (!st.noAutoFetch) {
			st.fetchDataEx();
			if (st.pollMs)
				st.tmPoll = setInterval(st.pollFetch, st.pollMs);
		}

		return () => {
			clearInterval(st.tmPoll);
			clearTimeout(st.tmPollSock);
			st.socket && appCtx.socket?.off(st.socket, st.cbSocket);   // if socket was on - disengage it to stop catching the old socket with its params.
		};
	}, [st.depOrd]);

	const {
		data: rsp, fetch, cancelFetch, isInit, refresh, isFetching, noSrvCache, error, isLoading, silentClear
	} = st;
	return {rsp, fetch, cancelFetch, isInit, refresh, isFetching, noSrvCache, error, isLoading, silentClear} as {
		rsp: R | null,
		fetch: (paramsAdd?: Partial<ApiParams<K>>, opts?: {recalc?: number | boolean}) => void,
		cancelFetch: () => void,
		isInit: boolean,
		refresh: (recalc?: number, paramsAdd?: Partial<ApiParams<K>>) => void,
		isFetching: () => boolean,
		noSrvCache: boolean | undefined,
		error: any,
		isLoading: boolean,
		silentClear: () => void
	};
}


export interface VirtualPagingParams<K extends keyof ApiParamsMap & string> extends FetchDataParams<K> {
	chunkLen?: number;
	debounceMs?: number;
	cacheMins?: number | boolean;
}

/**
 * Wrapper around useFetchData to handle paging for a virtual table. Fetches chunkLen=100 items each time.
 * Any array in the response object will be treated as a virtual table, and will be paginated, based on onPaging's startIndex and stopIndex.
 * silentClear(): purges current results should not return until a new fetch is performed.
 */
export function useVirtualPaging<K extends keyof ApiParamsMap & string>(
		{
			api, params, deps, socket, cacheMins = true, debounceMs = 100, chunkLen = 100, ...props
		}: VirtualPagingParams<K>) {
	const clearData = () => ({rsp: null, rows: {}, from: 0, pageLen: chunkLen});
	const {state: st} = useStateEx<any>({...clearData(), rspVer: 0, lastScrollTop: 0});
	Object.assign(st, {api, params, deps, clearData, props});
	const checkUpdDeps = () => componentInit(st, [funcOrValOr(st.api), ...funcOrValOr(st.deps) ?? []], '_init_deps');

	const {
		fetch: orgFetch, isFetching, cancelFetch, noSrvCache, error, isLoading
	} = useFetchData({
		api,
		deps,
		cacheMins,
		socket,
		keepWhileRefetching: true,
		params: () => ({...funcOrValOr(st.params), from: st.from, len: st.pageLen}),
		cacheKeep: () => !st.from,
		...props,       // order is important: cacheKeep above can be overridden, but onData below should override.
		onData: r => {
			r = st.props.onData? st.props.onData(r) : r;        // preprocess response?
			Object.entries(r).forEach(([k, v]) => {             // process fetched rows into existing array within st.rows
				if (!Array.isArray(v)) return;
				st.rows[k] ??= [];
				st.rows[k].splice(st.from, v.length, ...v);     // replace/append the new rows
				if (r.rowCount >= 0)
					st.rows[k].length = r.rowCount;
			});
			st.rspVer++;
			return st.rsp = {...r, ...st.rows};
		},
	});

	if (componentInit(st)) {
		st.onPaging = ({
			startIndex, stopIndex, scrollTop, scrollToPx, sclBarPx
		}) => {
			if (scrollTop !== undefined) st.lastScrollTop = scrollTop;

            // After refresh, restore scroll once data has landed. Require sclBarPx > 0: ensures content overflows (virtual block rendered with full height), otherwise scrollTop would clamp to 0.
			if (st.pendingResume !== undefined && st.rsp != null && sclBarPx > 0) {
				const arr = (st.rows.data ?? Object.values(st.rows)[0]) as [];
				const px = st.pendingResume;
				st.pendingResume = undefined;           // Clear before scrollToPx, otherwise the recursive onPaging re-enters this branch and recurses unboundedly.
				if (arr?.length && scrollToPx) {
					scrollToPx(px);
					return;                             // Recursive onPaging will run the fetch path against the restored viewport.
				}
			}

			const arr = (st.rows.data ?? Object.values(st.rows)[0]) as [];
			if (!arr || startIndex >= arr.length) return;

            // if current viewable rows are all available, do nothing:
			let from = startIndex + arr.slice(startIndex, stopIndex).findIndex(r => r === undefined);
			if (from < startIndex) return;                              // all rows requested are present

			let pageLen = Math.max(chunkLen, stopIndex - startIndex);
			let nMissing = arr.slice(from, from + pageLen).findIndex(r => r !== undefined);
			if (nMissing < chunkLen) {
				if (nMissing < 0)
					nMissing = Math.min(pageLen, arr.length - from);
				const rewind = arr.slice(from + nMissing - chunkLen, from).reverse().findIndex(r => r !== undefined);   // check backwards how many rows are missing
				from -= rewind >= 0? rewind : chunkLen - nMissing;      // fetch backwards as much as possible.
				if (rewind >= 0)                                        // reduce fetch amount if full chunkLen is not needed.
					pageLen = rewind + nMissing;
			}
			const isRecent = Date.now() - st.lastFetchAt < debounceMs;              // if recently tried to fetch the same chunk, skip.
			if ((isRecent || isFetching()) && st.from === from && st.pageLen === pageLen) return;

			Object.assign(st, {from, pageLen});
			cancelFetch();
			clearTimeout(st.tmDebounce);
			st.lastFetchAt = Date.now();
			if (!isRecent)
				orgFetch();
			else
				st.tmDebounce = setTimeout(orgFetch, debounceMs);
		};

		st.fetch = (paramsAdd, opts = {}) => {
			st.silentClear();
			checkUpdDeps();     // update init deps to prevent re-init.
			orgFetch(paramsAdd, opts);
		};

		st.silentClear = () => {
			if (!st.rsp) return;
			Object.assign(st, st.clearData());
			st.rspVer++;
		};

		st.refresh = () => {
			st.pendingResume = st.lastScrollTop;        // Snapshot before silentClear; the empty-interim browser-clamp would otherwise overwrite it.
			st.fetch(null, {recalc: 1});
		};
	}

	if (checkUpdDeps())
		st.silentClear();

	useEffect(() => () => clearTimeout(st.tmDebounce), []);

	const {
		rsp, rspVer, fetch, refresh, onPaging, silentClear
	} = st;
	return {rsp, rspVer, fetch, refresh, onPaging, silentClear, isFetching, noSrvCache, error, isLoading};
}


// trick: [st.cbItmToId? "map" : "valueOf"](st.cbItmToId)    // conditional method chaining instead of: .map(st.cbItmToId ?? (l => l)). valueOf doesn't care about its params.

export function useFetchTaskStats({
	api, params, cbAddParams, depsClear, depsFetch, list, cbItmToId, cbKey = undefined, noPaging, ...rest
}) {
	const {state: st, setState, rerender} = useStateEx<any>({});
	const {fetchData} = useFetchDataCb();
	Object.assign(st, {api, params, cbAddParams, list, cbItmToId, cbKey, rest});           // allow memoized functions to use latest versions.

	if (componentInit(st)) {
		st.onPagingStats = ({startIndex, stopIndex}) => {
			Object.assign(st, {startIndex, stopIndex});
			st.fetchPageTasks();
		};

		st.fetchPageTasks = () => {
			if (!st.api || st.tasksErr || !componentInit(st, [st.startIndex, st.stopIndex], '_checkLastQuery')) return;     // ignore if already fetching this range.

			const ids = st.list?.slice(st.startIndex, st.stopIndex || 0)
				.filter(l => l !== undefined)
				.map(st.cbItmToId)
				.filter(id => st.taskStats[st.cbKey?.(id) ?? id] === undefined);

			if (!ids?.length) return;
			fetchData({
				api: st.api,
				params: {...funcOrValOr(st.params), ...st.cbAddParams(ids)},
				...st.rest,
				onData: rsp => {
					rsp?.data.forEach(r => st.taskStats[st.cbKey?.(st.cbItmToId(r)) ?? st.cbItmToId(r)] = r);
					ids.forEach(id => (k => !st.taskStats[k] && (st.taskStats[k] = {}))(st.cbKey?.(id) ?? id));     // if server didn't return a value for this item, fake it. note: uses a function trick to minimize cbKey calls.
					rerender();
				},
				noErrMsg: true,
				onErr: tasksErr => setState({tasksErr}),
			});
		};
	}

	if (componentInit(st, depsClear, '_initClear')) {
		st.taskStats = [];
		st.onPagingStatsDebounced?.cancel();
		st.onPagingStatsDebounced = debounce(st.onPagingStats, 250, {ifInit: true});        // reset debouncer
	}

    // if viewed list items have changed, but items are fundamentally the same, keep taskStats, just fill items we're missing...
	if (componentInit(st, [...depsClear ?? [], ...depsFetch || []], '_initFetch')) {
		componentInit(st, [], '_checkLastQuery');       // clear lastQuery check.
		noPaging ? st.onPagingStats({startIndex: 0, stopIndex: st.list?.length}) :      // set to fetch all list
			st.fetchPageTasks();            // try to fetch with whatever sizing info we have at this point.
	}

	const {
		taskStats, onPagingStats, onPagingStatsDebounced, fetchPageTasks, tasksErr
	} = st;
	return {taskStats, onPagingStats, onPagingStatsDebounced, fetchPageTasks, tasksErr};
}

export const checkFileType = (onSelect) => (file, acceptType) => {
	if (!file) msgBox('No file selected.');
	if (file.name.toLowerCase().endsWith(acceptType) || acceptType === '*.*')
		onSelect(file);
	else
		msgBox(`Invalid file type. Please select a ${acceptType} file.`);

};
